import React from 'react';
import {COLORS, ELEVATIONS, SIZES} from '../styleVars';
import styled from 'styled-components';
import {Typography} from '../typography/Typography';

interface ContainerProps {
  justify: 'flex-start' | 'center' | 'flex-end';
  fullwidth: boolean;
  bgColor: string;
  elevation: string;
  focusColor: string;
}

const Container = styled.div<ContainerProps>`
  display: flex;
  justify-content: ${(props) => props.justify};
  width: ${(props) => (props.fullwidth ? '14.5rem' : 'auto')};

  button {
    width: inherit;
    display: flex;
    justify-content: ${(props) => (props.fullwidth ? 'flex-start' : 'center')};
    align-items: center;
    min-width: ${SIZES['72']};
    height: ${SIZES['36']};
    padding: 0 ${SIZES['16']};
    text-transform: uppercase;
    background-color: ${(props) => props.bgColor};
    border: none;
    border-radius: ${SIZES['2']};
    outline: none;
    -webkit-tap-highlight-color: transparent;
    box-shadow: ${(props) => props.elevation};
    cursor: pointer;
  }

  button:hover,
  button:focus {
    background-color: ${(props) => props.focusColor};
  }
`;

const IconContainer = styled.div`
  display: inline;
  align-items: center;
  height: ${SIZES['24']};
  width: ${SIZES['24']};
  margin-right: ${SIZES['12']};
  margin-left: -${SIZES['4']};
`;

const Label = styled(Typography)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

interface ButtonProps {
  /**
   * label of the button
   */
  label: string;
  /**
   * defines a text-button
   */
  text?: boolean;
  /**
   * defines a button with secondary styling
   */
  secondary?: boolean;
  /**
   * disables the button
   */
  disabled?: boolean;
  /**
   * defines an icon for the button
   */
  icon?: SVGElement;
  /**
   * callback-function when button is clicked
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  /**
   * horizontal justification of the button
   */
  justify?: 'flex-start' | 'center' | 'flex-end';
  /**
   * source of an image that is displayed instead of an icon
   */
  img?: string;
  /**
   * defines if button is fullwidth
   */
  fullwidth?: boolean;
  /**
   * tab-index of the Button, used to customize focus-order
   */
  tabIndex?: number;
  /**
   * type of the button
   */
  type?: 'button' | 'reset' | 'submit';
}

const Button: React.FC<ButtonProps> = ({
  label,
  text = false,
  secondary = false,
  disabled = false,
  tabIndex = 0,
  type = 'button',
  icon,
  onClick,
  justify = 'flex-start',
  img,
  fullwidth = false,
  ...props
}) => {
  let buttonConfig = {
    typoVariant: 'button-primary',
    backgroundColor: COLORS.fupaSecondary,
    elevation: ELEVATIONS.Button,
    focusColor: COLORS.fupaSecondaryFocused,
  };

  if (text && !disabled && secondary) {
    buttonConfig = {
      typoVariant: 'button-sub',
      backgroundColor: 'transparent',
      elevation: 'none',
      focusColor: COLORS.lighterGrey,
    };
  } else if (text && !disabled) {
    buttonConfig = {
      typoVariant: 'button-secondary',
      backgroundColor: 'transparent',
      elevation: 'none',
      focusColor: COLORS.lighterGrey,
    };
  } else if (text && disabled) {
    buttonConfig = {
      typoVariant: 'button-disabled',
      backgroundColor: 'transparent',
      elevation: 'none',
      focusColor: COLORS.lighterGrey,
    };
  } else if (secondary && !disabled) {
    buttonConfig = {
      typoVariant: 'button-secondary',
      backgroundColor: COLORS.lightestGrey,
      elevation: ELEVATIONS.Button,
      focusColor: COLORS.lighterGrey,
    };
  } else if (disabled) {
    buttonConfig = {
      typoVariant: 'button-disabled',
      backgroundColor: COLORS.lightestGrey,
      elevation: ELEVATIONS.Button,
      focusColor: COLORS.lighterGrey,
    };
  }

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (onClick) {
      onClick(e);
    }
  };

  let displayIcon;
  if (icon) {
    displayIcon = <IconContainer>{icon}</IconContainer>;
  } else if (img) {
    displayIcon = (
      <IconContainer>
        <img src={img} width={24} height={24} alt={label} />
      </IconContainer>
    );
  }

  return (
    <Container
      justify={justify}
      bgColor={buttonConfig.backgroundColor}
      elevation={buttonConfig.elevation}
      fullwidth={fullwidth}
      focusColor={buttonConfig.focusColor}>
      <button onClick={handleClick} disabled={disabled} type={type} tabIndex={tabIndex} {...props}>
        {displayIcon}
        <Label variant={buttonConfig.typoVariant}>{label}</Label>
      </button>
    </Container>
  );
};

export {Button};
